import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { LandingPageComponent } from "./features/landing-page/landing-page.component";
import { CanDeactivateGuard } from "./common/guards/unsaved-changes-guard";
import { AccessDeniedComponent } from "./features/access-denied/access-denied.component";
import { PayorFacilitiesComponent } from "./features/corporation-management/payor-management/payor-facilities/payor-facilities.component";
import { PayorFormComponent } from "./features/corporation-management/payor-management/payor-form-management.component";
import { PayorProfileComponent } from "./features/corporation-management/payor-management/payor-profile/payor-profile.component";
import { ValidateComponent } from "./features/validate/validate.component";
import { InitComponent } from "./features/init/init.component";
import { userValidatedGuard } from "./common/guards/user-validated.guard";
import { CoSignComponent } from "./features/co-sign/co-sign.component";
import { AutoForecastComponent } from "./features/patients/forecast/auto-forecast/auto-forecast.component";
import { BiDashboardComponent } from "./features/bi-dashboard/bi-dashboard.component";
import { appRoute } from "./app-route.constants";
import { FeatureAccessGuard, getFeatureAccessGuardData } from "./common/guards/feature-access.guard";
import { Features } from "./common/enums/features.enum";
import { mobileBlockGuard } from "./common/guards/mobile-block.guard";
import { fallbackRouteGuard } from "./common/guards/fallback-route.guard";
import { accessDeniedGuard } from "./common/guards/access-denied.guard";
import { homeFallbackGuard } from "./common/guards/home-fallback.guard";
import { MarketingComponent } from "./features/marketing/marketing.component";
import { TileComponent } from "./features/tiles/tile.component";

const routes: Routes = [
  {
    path: "validate",
    component: ValidateComponent,
  },
  {
    path: appRoute.init,
    component: InitComponent,
  },
  {
    path: appRoute.tile,
    component: TileComponent,
  },
  {
    path: appRoute.marketing,
    component: MarketingComponent,
    canActivate: [fallbackRouteGuard],
  },
  {
    path: "access-denied",
    component: AccessDeniedComponent,
    canActivate: [accessDeniedGuard],
  },
  {
    path: "",
    canActivate: [mobileBlockGuard, userValidatedGuard],
    children: [
      {
        path: "docreview",
        loadChildren: () => import("./features/esignature/esignature.routes").then((mod) => mod.esignatureRoutes),
      },
      {
        path: "practitioner",
        loadChildren: () => import("./features/practitioner-setup/routes").then((mod) => mod.practitionerRoutes),
      },
      {
        path: "facility",
        loadChildren: () => import("./features/facility/routes").then((mod) => mod.facilityRoutes),
      },
      {
        path: "patient",
        loadChildren: () => import("./features/patients/routes").then((mod) => mod.patientRoutes),
        canActivateChild: [userValidatedGuard],
      },
      {
        path: "user",
        loadChildren: () => import("./features/user-management/routes").then((mod) => mod.userRoutes),
      },
      {
        path: "corporation",
        loadChildren: () => import("./features/corporation-management/routes").then((mod) => mod.corporationRoutes),
      },
      {
        path: "security-group",
        loadChildren: () => import("./features/security-group/routes").then((mod) => mod.securityGroupRoutes),
      },
      {
        path: "time-card",
        loadChildren: () => import("./features/time-card/routes").then((mod) => mod.timeCardRoutes),
      },
      {
        path: "arc-codes",
        loadChildren: () => import("./features/arc-codes/routes").then((mod) => mod.arcCodeRoutes),
      },
      {
        path: "payor/:payorId",
        component: PayorProfileComponent,
        canDeactivate: [CanDeactivateGuard],
        canActivate: [FeatureAccessGuard],
        data: getFeatureAccessGuardData(Features.CORP_SETTINGS_PAYOR_PLAN),
        children: [
          {
            path: "settings",
            component: PayorFormComponent,
            canDeactivate: [CanDeactivateGuard],
          },
          {
            path: "facilities",
            canDeactivate: [CanDeactivateGuard],
            component: PayorFacilitiesComponent,
          },
          { path: "", redirectTo: "settings", pathMatch: "full" },
        ],
      },
      {
        path: "esignature",
        loadChildren: () => import("./features/settings/routes").then((mod) => mod.settingsRoutes),
      },
      {
        path: "set-up",
        loadChildren: () => import("./features/set-up/routes").then((mod) => mod.setUpRoutes),
      },
      {
        path: "agenda",
        loadChildren: () => import("./features/agenda/routes").then((mod) => mod.agendaRoutes),
      },
      {
        path: "reports",
        loadChildren: () => import("./features/reports/routes").then((mod) => mod.reportRoutes),
      },
      {
        path: appRoute.biDashboard,
        component: BiDashboardComponent,
      },
      {
        path: "home",
        component: LandingPageComponent,
      },
      {
        path: "co-sign",
        component: CoSignComponent,
      },
      {
        path: "forecast-template/:templateId",
        component: AutoForecastComponent,
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: "close-out",
        loadChildren: () => import("./features/close-out/routes").then((mod) => mod.closeOutRoutes),
      },
      { path: "**", canActivate: [homeFallbackGuard], children: [] },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { paramsInheritanceStrategy: "always" })],
  exports: [RouterModule],
})
export class AppRoutingModule {}